import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"

import { getCookie, trimRupeeSymbol } from "@commonUtils"

import { pushToGTM } from "@lib/gtmUtility"
import { DEFAULT_ERROR_MESSAGE, SNACK_BAR_DELAY_TIME } from "./constants"
import { sendMixpanelTrackEvent } from "@lib/gtmUtility"

const confirmDialogInitialState = {
    sku: {},
    showConfirmDialog: false,
    confirmDialogType: "",
    heading: "",
    subHeading: "",
    primaryCTALabel: "",
    secondaryCTALabel: "",
    icon: "",
}

const initialState = {
    visitorId: null,
    isBot: false,
    userData: null,
    loggedIn: false,
    cartCount: 0,
    itemsInCart: [],
    itemsInDoneInOneCart: {},
    cartAmount: null,
    fetchingCart: false,
    carePlanData: null,
    cartId: null,
    snackBarMessage: null,
    cartType: "pharmacy",

    //labs
    labsCartId: null,
    labsCartCount: 0,
    itemsInLabsCart: [],
    labsItemCategory: null,
    labsCartAmount: null,
    labId: null,
    haveLabsSKUs: null,

    //lab skus confirm dialog config
    confirmDialogConfig: confirmDialogInitialState,

    // Main header toggles
    showNavigationLinks: true,
    showSearch: false,
    showProfile: true,
    showOffers: true,
    showCart: true,
    showHelp: true,
    showCorporateDashboardLink: false,

    // Sub header toggles
    showSubHeader: true,
    showSubLocation: true,
    showSubSearch: true,
    showSubQuickOrder: true,

    // Category header toggles
    showCategoryTree: true,
    authConfig: {
        showAuth: false,
        onClose: null,
        onSuccess: null,
        onFailure: null,
        populateData: null,
    },
    isLogin: true,
    showFooter: true,
    showHeader: true,
    pageName: "",
}

export function trackAddToCartWidget(label, category, action) {
    const gaConfig = {
        label: label,
        category: category,
        action: action,
    }
    pushToGTM(gaConfig)
}

export const parseAddTestError = ({
    type,
    data: { header, description, icon, confirm_cta, cancel_cta } = {},
}) => {
    return {
        confirmDialogType: type,
        heading: header,
        subHeading: description,
        imageUrl: icon,
        primaryCtaLabel: confirm_cta?.text || "",
        secondaryCtaLabel: cancel_cta?.text || "",
    }
}

// this parser should be used everywhere to store itemsInCart in shellReducer.
export const parseItemsInCartInBriefResponse = (data) => {
    let items = {}
    if (data) {
        data.forEach((item) => {
            items[item.sku_id] = { quantity: item.quantity, skuId: item.sku_id, name: item.sku_name }
        })
    }

    return items
}

export const parseItemsInCartInFullResponse = (data) => {
    let items = {}

    data?.forEach((groupedItem) => {
        groupedItem?.items?.forEach((item) => {
            items[item?.cta?.details?.sku_id] = {
                quantity: item?.cta?.details?.quantity,
                skuId: item?.cta?.details?.sku_id,
            }
        })
    })
    return items
}

export const parseCartInBriefResponseType = (response) => {
    const cartData = {
        itemsInCart: parseItemsInCartInBriefResponse(response?.data?.brief?.items),
        cartAmount: response?.data?.brief?.to_be_paid,
        cartCount: response?.data?.brief?.total_quantity,
        cartId: response?.data?.brief?.cart_id,
    }
    return cartData
}

export const parseCartInFullResponseType = (response) => {
    let cartAmount, groupedItems, itemsInCart
    response?.data?.widgets?.forEach((widget) => {
        if (widget?.type === "cart_items") {
            groupedItems = widget?.data?.grouped_items
        }
        if (widget?.type === "bill_summary") {
            cartAmount = widget?.data?.payable_amount
        }
    })
    itemsInCart = parseItemsInCartInFullResponse(groupedItems)
    const cartData = {
        itemsInCart: itemsInCart,
        cartAmount: cartAmount,
        cartCount: Object.keys(itemsInCart)?.length,
        cartId: response?.data?.cart_id,
    }
    return cartData
}

export const parseItemsInDoneInOneCart = (response) => {
    let groupedItems, itemsInDoneInOneCart
    response?.data?.widgets?.forEach((widget) => {
        if (widget?.type === "cart_items") {
            groupedItems = widget?.data?.grouped_items
        }
    })
    itemsInDoneInOneCart = parseItemsInCartInFullResponse(groupedItems)

    return itemsInDoneInOneCart
}

// Labs parsers
export const parseItemsInLabsCartInBriefResponse = (data) => {
    let items = {}
    if (data) {
        data.forEach((item) => {
            if (item?.id) {
                items[item.id] = {
                    header: item.heading,
                    skuId: item.id,
                    image: {
                        url: item.icon_url,
                        alt: "",
                    },
                    subtext: item?.sub_heading,
                    prices: {
                        discountPercent: item.price?.discount_percent,
                        discountedPrice: item.price?.discounted_price,
                        price: item.price?.mrp,
                    },
                    patientIds: item.patient_ids,
                    recommendationType: item.recommendation_type,
                    mixPanelData: item.mix_panel_data,
                }
            }
        })
    }
    return items
}

export const parseItemsInLabsCartInFullResponse = (data) => {
    let items = {}

    data?.forEach((item) => {
        if (item?.sku_id) {
            items[item.sku_id] = {
                skuId: item.sku_id,
                header: item.header,
                image: item.image,
                prices: {
                    discountPercent: item.prices?.discount,
                    discountedPrice: item.prices?.discounted_price,
                    price: item.prices?.mrp,
                },
                patientIds: item.patients_data,
            }
        }
    })
    return items
}

export const parseLabsCartInBriefResponseType = (response) => {
    const itemsInLabsCart = parseItemsInLabsCartInBriefResponse(response?.data?.brief?.items)
    const cartData = {
        itemsInLabsCart: itemsInLabsCart,
        labsCartCount: response?.data?.brief?.cart_count ?? Object.keys(itemsInLabsCart)?.length,
        labsCartId: response?.data?.brief?.cart_id,
        labId: response?.data?.brief?.lab_id,
        labsCartAmount: response?.data?.brief?.cart_value,
        labsItemCategory: response?.data?.brief?.category?.toLowerCase(),
    }
    return cartData
}

export const parseLabsCartInFullResponseType = (response) => {
    let labsCartAmount, items, itemsInLabsCart, labId
    response?.data?.widgets?.forEach((widget) => {
        if (widget?.type === "list_tests_cart") {
            items = widget?.data?.items
            labId = widget?.data?.lab_id
        }
        if (widget?.type === "bill_summary") {
            labsCartAmount = widget?.data?.payable_amount
        }
    })
    itemsInLabsCart = parseItemsInLabsCartInFullResponse(items)
    const cartData = {
        itemsInLabsCart: itemsInLabsCart,
        labsItemCategory: response?.data?.category?.toLowerCase(),
        labsCartAmount: labsCartAmount,
        labsCartCount: Object.keys(itemsInLabsCart)?.length,
        labsCartId: response?.data?.cart_id,
        labId: labId,
    }
    return cartData
}

const parseGaDataForAddToCart = (gaDataFromAPI, gaDataFromPage, completeCart, sku = null) => {
    let gaAction = completeCart ? "Add to cart" : "Add test"

    if (sku?.page !== "search" && gaDataFromPage?.category !== "Diagnostics Category Listing") {
        if (gaDataFromPage?.footer) {
            gaAction += " Footer"
        } else if (gaDataFromPage?.widget) {
            gaAction += " - Widget"
        } else if (gaDataFromPage?.bottomsheet) {
            gaAction += " - bottom sheet"
        }
    }
    let newGaDataFromAPI = gaDataFromAPI?.label
    if (!completeCart && gaDataFromPage?.category !== "Diagnostics Search Page") {
        newGaDataFromAPI.entity_price =
            (gaDataFromPage.label?.entity_price &&
                parseInt(trimRupeeSymbol(gaDataFromPage.label.entity_price))) ||
            0
        newGaDataFromAPI.entity_discounted_price =
            (gaDataFromPage.label?.entity_discounted_price &&
                parseInt(trimRupeeSymbol(gaDataFromPage.label.entity_discounted_price))) ||
            0
    }
    const gaData = {
        label: {
            ...(gaDataFromPage?.label || {}),
            ...(newGaDataFromAPI || {}),
        },
        category: gaDataFromPage?.category || "",
        action: gaAction,
    }
    return gaData
}

export const showSnackBar = (dispatch, message, time = 3000) => {
    dispatch(setSnackBarMessage(message))
    setTimeout(() => {
        dispatch(setSnackBarMessage(null))
    }, time)
}

export const appSlice = createSlice({
    name: "shellReducer",
    initialState: initialState,
    reducers: {
        updateUserData: (state, action) => {
            state.userData = action.payload
        },
        setLoggedIn: (state, action) => {
            state.loggedIn = action.payload
        },
        setCorporateDetails: (state, action) => {
            state.corporateDetails = action.payload
        },
        setVisitorId: (state, action) => {
            state.visitorId = action.payload
        },
        setIsBot: (state) => {
            state.isBot = true
        },
        updateProductMetaData: (state, action) => {
            state.meta_data = action.payload
        },
        updateCartCount: (state, action) => {
            state.cartCount = action.payload
        },
        updateCartAmount: (state, action) => {
            state.cartAmount = action.payload
        },
        updateItemsInCart: (state, action) => {
            state.itemsInCart = action.payload
        },
        setCartId: (state, action) => {
            state.cartId = action.payload
        },
        updateCartData: (state, action) => {
            state.cartCount = action.payload?.cartCount
            state.cartAmount = action.payload?.cartAmount
            state.itemsInCart = action.payload?.itemsInCart
            state.cartId = action.payload?.cartId
        },
        updateLabsCartData: (state, action) => {
            state.labsCartCount = action.payload?.labsCartCount
            state.labsCartAmount = action.payload?.labsCartAmount
            state.itemsInLabsCart = action.payload?.itemsInLabsCart
            state.haveLabsSKUs = checkSelectedSKUs(action.payload?.itemsInLabsCart)
            state.labsItemCategory = action.payload?.labsItemCategory?.toLowerCase()
            state.labsCartId = action.payload?.labsCartId
            state.labId = action.payload?.labId
        },
        setSnackBarMessage: (state, action) => {
            state.snackBarMessage = action.payload
        },
        setShowNavigationLinks: (state, action) => {
            state.showNavigationLinks = action.payload
        },
        setShowSearch: (state, action) => {
            state.showSearch = action.payload
        },
        setShowProfile: (state, action) => {
            state.showProfile = action.payload
        },
        setShowOffers: (state, action) => {
            state.showOffers = action.payload
        },
        setShowCart: (state, action) => {
            state.showCart = action.payload
        },
        setShowHelp: (state, action) => {
            state.showHelp = action.payload
        },
        setShowSubHeader: (state, action) => {
            state.showSubHeader = action.payload
        },
        setShowSubLocation: (state, action) => {
            state.showSubLocation = action.payload
        },
        setShowSubSearch: (state, action) => {
            state.showSubSearch = action.payload
        },
        setShowSubQuickOrder: (state, action) => {
            state.showSubQuickOrder = action.payload
        },
        setShowCategoryTree: (state, action) => {
            state.showCategoryTree = action.payload
        },
        setShowCorporateDashboardLink: (state, action) => {
            state.showCorporateDashboardLink = action.payload
        },
        updateItemsInDoneInOneCart: (state, action) => {
            state.itemsInDoneInOneCart = action.payload
        },
        setShowFooter: (state, action) => {
            state.showFooter = action.payload
        },
        setShowHeader: (state, action) => {
            state.showHeader = action.payload
        },
        setPageName: (state, action) => {
            state.pageName = action.payload
        },
        setDefaultAuthConfig: (state) => {
            state.authConfig = {
                showAuth: false,
                onClose: null,
                onSuccess: null,
                onFailure: null,
                populateData: null,
                authType: "",
                restrictUserFromClosingAuth: false,
            }
        },
        updateAuthConfig: (state, action) => {
            state.authConfig = {
                ...action.payload,
            }
        },
        setIsLogin: (state, action) => {
            state.isLogin = action.payload
        },
        setCartType: (state, action) => {
            state.cartType = action.payload
        },
        showConfirmDialog: (state, action) => {
            state.confirmDialogConfig = action.payload
        },
        hideConfirmDialog: (state) => {
            state.confirmDialogConfig = confirmDialogInitialState
        },
        setHeaderLogoUrl: (state, action) => {
            state.headerLogoUrl = action.payload
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getCart.fulfilled, (state, action) => {
            const data = action.payload.data?.brief
            state.fetchingCart = false
            state.cartCount = data?.total_quantity
            state.itemsInCart = parseItemsInCartInBriefResponse(data?.items)
            state.carePlanData = data.care_plan_header
            state.cartId = data?.cart_id
            state.cartAmount = data?.total_amount
        }),
            builder.addCase(getCart.pending, (state) => {
                state.fetchingCart = true
            }),
            builder.addCase(getCart.rejected, (state) => {
                state.fetchingCart = false
            }),
            builder.addCase(getLabsCart.fulfilled, (state, action) => {
                const data = action.payload.data
                const itemsInLabsCart = parseItemsInLabsCartInBriefResponse(data.items)

                state.fetchingCart = false
                state.itemsInLabsCart = itemsInLabsCart
                state.haveLabsSKUs = checkSelectedSKUs(data.items)
                state.labsItemCategory = data?.category?.toLowerCase()
                state.labsCartCount = data?.cart_count ?? Object.keys(itemsInLabsCart)?.length
                state.labsCartId = data?.cart_id
                state.labId = data?.lab_id
                state.labsCartAmount = data?.cart_value
            }),
            builder.addCase(getLabsCart.pending, (state) => {
                state.fetchingCart = true
            }),
            builder.addCase(getLabsCart.rejected, (state) => {
                state.fetchingCart = false
            })
    },
})

export const getCart = createAsyncThunk("shellReducer/getCart", async (_arg, { extra }) => {
    const { api } = extra
    const currentCity = getCookie("city")
    return await api.get("/api/v4/carts/details?types=pharmacy&response_type=brief", {
        params: {
            cart_oos: true,
            city: currentCity,
        },
    })
})

export const getLabsCart = createAsyncThunk("shellReducer/getLabsCart", async (_arg, { extra }) => {
    const { api } = extra
    return await api.get("/api/v4/carts/brief?types=labs")
})

export const replaceLabsCart = createAsyncThunk(
    "shellReducer/replaceLabsCart",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { params, onReplace, gaDataFromPage, mixPanelData } = _arg

        let payload = {
            sku_ids: (() => {
                const skuList = []
                for (let skuId in params.sku_ids) {
                    skuList.push({
                        test_id: params.sku_ids[skuId],
                    })
                }
                return skuList
            })(),
        }

        const url = "api/v4/carts/replace?types=labs"

        const { api } = extra

        try {
            const response = await api.put(url, payload)
            if (response?.is_success) {
                if (gaDataFromPage) {
                    const gaData = parseGaDataForAddToCart(
                        response?.data?.analytics_data || {},
                        gaDataFromPage,
                        !response?.data?.is_partial_cart
                    )
                    trackAddToCartWidget(gaData?.label, gaData?.category, gaData?.action)
                }
                if (mixPanelData) {
                    const eventName = response?.data?.is_partial_cart ? "sku_added" : "sku_added_to_cart"

                    sendMixpanelTrackEvent(eventName, {
                        ...mixPanelData,
                        lab_id: response?.data?.lab_id,
                        lab_name: response?.data?.lab_name,
                    })
                }

                dispatch(getLabsCart())
                onReplace?.()

                return Promise.resolve(response?.data)
            }
        } catch (err) {
            showSnackBar(dispatch, DEFAULT_ERROR_MESSAGE, SNACK_BAR_DELAY_TIME)
            return rejectWithValue(err)
        }
    }
)

export const handleAuthentication = (args) => {
    const {
        onSuccess = null,
        onFailure = null,
        onClose = null,
        restrictUserFromClosingAuth = false,
    } = args ?? {}
    return (dispatch) => {
        dispatch(
            updateAuthConfig({
                showAuth: true,
                onSuccess,
                onFailure,
                onClose,
                restrictUserFromClosingAuth,
            })
        )
    }
}

function checkSelectedSKUs(selectedSKUs) {
    const skus = selectedSKUs || {}

    return Object.keys(skus).length > 0
}

export const addRequiredReducers = (reducer) => {
    return (dispatch, getState, { addReducer }) => {
        return addReducer(reducer)
    }
}

export const {
    updateUserData,
    setLoggedIn,
    setCorporateDetails,
    setShowCorporateDashboardLink,
    setSuperLoggedIn,
    setVisitorId,
    setIsBot,
    updateProductMetaData,
    updateCartCount,
    updateCartAmount,
    updateItemsInCart,
    setCartId,
    updateCartData,
    updateLabsCartData,
    updateItemsInDoneInOneCart,
    setSnackBarMessage,

    // Main header toggle setters
    setShowNavigationLinks,
    setShowSearch,
    setShowProfile,
    setShowOffers,
    setShowCart,
    setShowHelp,

    // Sub header toggle setters
    setShowSubHeader,
    setShowSubLocation,
    setShowSubSearch,
    setShowSubQuickOrder,

    // Category header toggle setters
    setShowCategoryTree,
    updateAuthConfig,
    setDefaultAuthConfig,
    setIsLogin,
    setShowFooter,
    setShowHeader,
    setPageName,
    setCartType,
    setHeaderLogoUrl,

    // Confirm Dialog actions
    showConfirmDialog,
    hideConfirmDialog,
} = appSlice.actions
export const shellReducer = appSlice.reducer
